// Easy Remote Control Setup: robot layouts drawn top-down in a 200 x 200 box (front at the top).
export const PORT_LETTERS = ["A", "B", "C", "D", "E", "F"];

const motor = (id, label, side, port, dir, badge, extra = {}) => ({ id, label, side, default: { port, dir }, badge, ...extra });
const wheel = (x, y, rot, motorId) => ({ x, y, rot, motor: motorId });
// Omni wheels roll along the clockwise tangent of their position (angle measured clockwise from the front).
const omniWheel = (m, x, y) => wheel(x, y, m.angle + 90, m.id);

// Direction a wheel pushes the robot (x right, y forward) when its motor spins forwards.
export const heading = (angle) => {
  const a = (angle * Math.PI) / 180;
  return [Math.cos(a), -Math.sin(a)];
};

const X_MOTORS = [
  motor("front_left", "Front left", null, "A", "F", [72, 72], { angle: -45 }),
  motor("front_right", "Front right", null, "B", "F", [128, 72], { angle: 45 }),
  motor("back_left", "Back left", null, "C", "F", [72, 128], { angle: -135 }),
  motor("back_right", "Back right", null, "D", "F", [128, 128], { angle: 135 }),
];

const KIWI_MOTORS = [
  motor("front_left", "Front left", null, "A", "F", [74, 81], { angle: -60 }),
  motor("front_right", "Front right", null, "B", "F", [126, 81], { angle: 60 }),
  motor("back", "Back", null, "C", "F", [100, 132], { angle: 180 }),
];
const KIWI_XY = { front_left: [36, 59], front_right: [164, 59], back: [100, 170] };

const STEER = motor("steer", "Steering", null, "C", "F", [100, 62], { steer: true });
const CAR_BODY = { type: "rect", x: 56, y: 30, w: 88, h: 140 };
const CAR_FRONT_WHEELS = [wheel(42, 62, 0, "steer"), wheel(158, 62, 0, "steer")];

export const DRIVETRAINS = [
  {
    id: "tank2", kind: "tank", anim: "tank", name: "2-motor drive", subtitle: "Tank drive", projectName: "2-motor tank robot",
    body: { type: "rect", x: 56, y: 36, w: 88, h: 128 }, front: [100, 48], links: [],
    motors: [motor("left", "Left", "left", "A", "R", [76, 100]), motor("right", "Right", "right", "B", "F", [124, 100])],
    wheels: [wheel(42, 100, 0, "left"), wheel(158, 100, 0, "right")],
  },
  {
    id: "tank4", kind: "tank", anim: "tank", name: "4-motor drive", subtitle: "Tank drive", projectName: "4-motor tank robot",
    body: { type: "rect", x: 56, y: 30, w: 88, h: 140 }, front: [100, 42], links: [],
    motors: [
      motor("front_left", "Front left", "left", "A", "R", [76, 64]),
      motor("back_left", "Back left", "left", "C", "R", [76, 136]),
      motor("front_right", "Front right", "right", "B", "F", [124, 64]),
      motor("back_right", "Back right", "right", "D", "F", [124, 136]),
    ],
    wheels: [wheel(42, 64, 0, "front_left"), wheel(42, 136, 0, "back_left"), wheel(158, 64, 0, "front_right"), wheel(158, 136, 0, "back_right")],
  },
  {
    id: "xdrive", kind: "omni", anim: "omni", name: "4-motor holonomic", subtitle: "Omnidirectional X-drive", projectName: "X-drive robot",
    body: { type: "rect", x: 50, y: 50, w: 100, h: 100 }, front: [100, 60], links: [],
    motors: X_MOTORS,
    wheels: [omniWheel(X_MOTORS[0], 48, 48), omniWheel(X_MOTORS[1], 152, 48), omniWheel(X_MOTORS[2], 48, 152), omniWheel(X_MOTORS[3], 152, 152)],
  },
  {
    id: "kiwi", kind: "omni", anim: "omni", name: "3-motor holonomic", subtitle: "Omnidirectional kiwi drive", projectName: "Kiwi drive robot",
    body: { type: "poly", points: "36,59 164,59 100,170" }, front: [100, 68], links: [],
    motors: KIWI_MOTORS,
    wheels: KIWI_MOTORS.map((m) => omniWheel(m, ...KIWI_XY[m.id])),
  },
  {
    id: "car2", kind: "car", anim: "car", name: "Car steering, 2 drive motors", subtitle: "1 steering motor + 2 drive motors", projectName: "Steering car, 2 drive motors",
    body: CAR_BODY, front: [100, 40], links: [[42, 62, 158, 62]],
    motors: [STEER, motor("left", "Back left", "left", "A", "R", [76, 140]), motor("right", "Back right", "right", "B", "F", [124, 140])],
    wheels: [...CAR_FRONT_WHEELS, wheel(42, 140, 0, "left"), wheel(158, 140, 0, "right")],
  },
  {
    id: "car1", kind: "car", anim: "car", name: "Car steering, 1 drive motor", subtitle: "1 steering motor + 1 drive motor", projectName: "Steering car, 1 drive motor",
    body: CAR_BODY, front: [100, 40], links: [[42, 62, 158, 62], [42, 140, 158, 140]],
    motors: [STEER, motor("drive", "Drive", null, "A", "F", [100, 140])],
    wheels: [...CAR_FRONT_WHEELS, wheel(42, 140, 0, "drive"), wheel(158, 140, 0, "drive")],
  },
];

export const drivetrainById = (id) => DRIVETRAINS.find((d) => d.id === id) || DRIVETRAINS[0];

export const STYLES = {
  arcade_left: { name: "Left stick arcade", desc: "The left stick does everything: push to drive, tilt to turn.", left: { axes: "both", label: "Drive + turn" }, right: null },
  arcade_right: { name: "Right stick arcade", desc: "The right stick does everything: push to drive, tilt to turn.", left: null, right: { axes: "both", label: "Drive + turn" } },
  split_left: { name: "Split arcade: left stick drives", desc: "Left stick forward and back, right stick left and right.", left: { axes: "y", label: "Forward / back" }, right: { axes: "x", label: "Turn" } },
  split_right: { name: "Split arcade: right stick drives", desc: "Right stick forward and back, left stick left and right.", left: { axes: "x", label: "Turn" }, right: { axes: "y", label: "Forward / back" } },
  tank: { name: "Tank drive", desc: "Left stick up/down drives the left wheels, right stick the right wheels.", left: { axes: "y", label: "Left wheels" }, right: { axes: "y", label: "Right wheels" } },
  omni_left: { name: "Left stick moves, right stick turns", desc: "Slide any direction with the left stick, spin on the spot with the right.", left: { axes: "both", label: "Move any direction" }, right: { axes: "x", label: "Rotate" } },
  omni_right: { name: "Right stick moves, left stick turns", desc: "Slide any direction with the right stick, spin on the spot with the left.", left: { axes: "x", label: "Rotate" }, right: { axes: "both", label: "Move any direction" } },
};

export const STYLES_BY_KIND = {
  tank: ["arcade_left", "arcade_right", "split_left", "split_right", "tank"],
  car: ["arcade_left", "arcade_right", "split_left", "split_right"],
  omni: ["omni_left", "omni_right"],
};
export const DEFAULT_STYLE = { tank: "split_left", car: "split_left", omni: "omni_left" };
export const MOTION_TEXT = { tank: "Drives, then spins on the spot", omni: "Slides any way without turning", car: "Steers its front wheels like a car" };

// "Reverse" means flipped from the usual build (e.g. a tank's left motor is mounted mirrored).
export const isFlipped = (m, dir) => dir !== m.default.dir;
export const pushVector = (m, dir) => heading(m.angle).map((v) => (dir === "R" ? -v : v));
export const pushAngle = ([x, y]) => (Math.atan2(x, y) * 180) / Math.PI;
const COMPASS = ["forwards", "forward-right", "right", "back-right", "backwards", "back-left", "left", "forward-left"];
export const compassWord = (v) => COMPASS[(Math.round(pushAngle(v) / 45) + 8) % 8];

const carWords = (label) => label.replace("turn", "steer").replace("Turn", "Steer");

export const styleRoles = (styleId, kind) => {
  const s = STYLES[styleId];
  if (!s) return { left: null, right: null };
  const fix = (r) => (r && kind === "car" ? { ...r, label: carWords(r.label) } : r);
  return { left: fix(s.left), right: fix(s.right) };
};

export const styleText = (styleId, kind) => {
  const s = STYLES[styleId];
  return kind === "car" ? { name: s.name, desc: carWords(s.desc) } : s;
};

export const defaultConfig = (drivetrainId, previousStyle) => {
  const dt = drivetrainById(drivetrainId);
  return {
    version: 1,
    drivetrain: dt.id,
    motors: Object.fromEntries(dt.motors.map((m) => [m.id, { ...m.default }])),
    steerMax: 45,
    style: STYLES_BY_KIND[dt.kind].includes(previousStyle) ? previousStyle : DEFAULT_STYLE[dt.kind],
    attachments: [],
  };
};

// Ports picked more than once, across drivetrain and attachment motors.
export const portConflicts = (cfg) => {
  const seen = new Set();
  const dupes = new Set();
  const ports = [...Object.values(cfg.motors).map((m) => m.port), ...(cfg.attachments || []).flatMap((a) => a.ports)];
  ports.forEach((port) => (seen.has(port) ? dupes.add(port) : seen.add(port)));
  return dupes;
};

export const rcProjectName = (cfg) => drivetrainById(cfg.drivetrain).projectName;

// A new setup starts empty: the drivetrain and the controller layout are the user's choice.
export const blankConfig = () => ({ version: 1, drivetrain: null, motors: {}, steerMax: 45, style: null, attachments: [] });
